import { getNormalizedFormData } from "./normalizers"
import { log } from "./log"

/**
 * Callbacks for different lifecycles of a form submission.
 */
export interface OnSubmitCallbacks {
  onSubmitEnd?: (status: number) => void
  onSubmitFailure?: (status?: number) => void
  onSubmitStart?: () => void
  onSubmitSuccess?: () => void
}

/**
 * Submit an HTML form.
 *
 * @param formData a FormData object
 * @param formUrl the form action URL
 * @returns a response promise
 */
export const submitForm = async (formData, formUrl) => {
  return await fetch(formUrl, {
    headers: {
      Accept: "application/json",
    },
    body: formData,
    method: "POST",
  })
}

/**
 * Manually submit an HTML form.
 *
 * @param form an HTML form element
 * @param callbacks functions to call before and after submission
 * @param env the current node environment (for debugging)
 * @returns the form submission's status, a Promise<number>
 */
export const handleFormSubmit = async (
  form: HTMLFormElement,
  callbacks: OnSubmitCallbacks = {},
  env?: string
) => {
  if (!form) {
    log(`No form element`, `error`, env)
    return
  }

  if (form.tagName !== `FORM`) {
    log([`Element isn't a form:`, form], `error`, env)
    return
  }

  const formUrl = form.getAttribute(`action`)
  try {
    new URL(formUrl)
  } catch {
    log([`Invalid action for form`, form], `error`, env)
    return
  }

  callbacks.onSubmitStart?.()

  const formData = getNormalizedFormData(form)
  log([`FormData:`, formData], `debug`, env)

  const { status } = await submitForm(formData, formUrl)
  log([`Request status:`, status], `debug`, env)

  callbacks.onSubmitEnd?.(status)

  if (status === 200) {
    callbacks.onSubmitSuccess?.()
    return
  }

  callbacks.onSubmitFailure?.(status)
}

export default {
  submitForm,
  handleFormSubmit,
}
